'use client';

import { cva } from 'class-variance-authority';
import { cn, Icon } from '@bridebook/aura';
import { forwardRef, type ComponentPropsWithoutRef, ElementRef, ComponentProps } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check } from '@bridebook/aura/src/icons';

type CheckboxSize = 'standard' | 'sm';

export type CheckboxProps = ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
  size?: CheckboxSize;
  error?: boolean;
};

export type CheckboxOnChange = NonNullable<ComponentProps<typeof Checkbox>['onCheckedChange']>;
export type CheckboxCheckedState = CheckboxPrimitive.CheckedState;

const checkboxVariants = cva(
  // Base
  'peer shrink-0 border-1 border-space-40 bg-white outline-none hover:border-indigo-crush focus-visible:ring-[3px] focus-visible:ring-focus-ring aria-invalid:border-errors ' +
    // Checked
    ' [&[data-state=checked]:not([data-disabled])]:border-indigo-crush [&[data-state=checked]:not([data-disabled])]:bg-indigo-crush [&[data-state=checked]:not([data-disabled])]:hover:border-indigo-crush-80' +
    // Disabled
    ' disabled:cursor-not-allowed data-[disabled]:border-space-15 data-[disabled]:bg-space-08' +
    // Disabled checked
    ' data-[state=checked][data-disabled]:hover:border-transparent data-[state=checked]:disabled:border-transparent data-[state=checked]:disabled:bg-indigo-crush/40',
  {
    variants: {
      size: {
        standard: 'h-6 w-6 rounded-lg',
        sm: 'h-4 w-4 rounded-md',
      },
    },
  },
);

const iconSize: Record<CheckboxSize, number> = {
  standard: 16,
  sm: 10,
};

export const Checkbox = forwardRef<ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  ({ className, size = 'standard', disabled, error = false, id, ...props }, ref) => (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      ref={ref}
      className={cn(checkboxVariants({ size }), className)}
      disabled={disabled}
      aria-invalid={error}
      id={id}
      {...props}>
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="flex h-full items-center justify-center">
        <Icon size={iconSize[size]} icon={Check} className="stroke-2 text-white" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  ),
);
Checkbox.displayName = CheckboxPrimitive.Root.displayName;
